import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Button,
  Link,
  useToast,
  Card,
  CardBody,
  Icon,
  Divider,
  Select,
  Checkbox,
  CheckboxGroup,
  SimpleGrid,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { FaEye, FaEyeSlash, FaServer, FaGoogle, FaGithub, FaUser, FaEnvelope, FaLock } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import Turnstile from "react-turnstile";
import { TURNSTILE_SITE_KEY } from "../config";
import { UNDER_CONSTRUCTION, ALLOWED_EMAIL } from '../App';



const CreateUser = () => {  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    organization: '',
    role: '',
    researchArea: '',
    agreeToTerms: false,
    subscribeNewsletter: false,
    job_notification: true,              // Add this
    credit_expiry_notification: true,    // Add this
  });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const { register } = useAuth();
  const [turnstileToken, setTurnstileToken] = useState("");
  const { isOpen: isSuccessOpen, onOpen: onSuccessOpen, onClose: onSuccessClose } = useDisclosure();
  const [registeredEmail, setRegisteredEmail] = useState('');

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    if (!formData.firstName || !formData.lastName || !formData.email) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }


    if (!formData.agreeToTerms) {
      toast({
        title: 'Error',
        description: 'You must agree to the terms and conditions.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (UNDER_CONSTRUCTION && formData.email.trim().toLowerCase() !== ALLOWED_EMAIL) {
      toast({
        title: 'Temporarily Unavailable',
        description: 'Account creation is temporarily disabled while we improve SRS. Please check back soon.',
        status: 'warning',
        duration: 6000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);

    try {
      // Call the API to register the user
      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,        
        organization: formData.organization,
        role: formData.role,
        researchArea: formData.researchArea,
        job_notification: formData.job_notification,
        credit_expiry_notification: formData.credit_expiry_notification,
        newsletter_subscription: formData.subscribeNewsletter  // Add this line
      };
      // Use the API service directly instead of the AuthContext register
      const response = await apiService.registerUser(userData);
      
      setRegisteredEmail(formData.email);
      onSuccessOpen();
    } catch (error) {
      console.error('Registration error:', error);
      
      let errorMessage = 'An error occurred while creating your account.';
      
      // Handle specific error cases
      if (error.message.includes('Username already registered')) {
        errorMessage = 'An account with this email already exists.';
      } else if (error.message.includes('Email already registered')) {
        errorMessage = 'This email address is already registered.';
      } else if (error.message.includes('400')) {
        errorMessage = 'Please check your input and try again.';
      } else if (error.message.includes('500')) {
        errorMessage = 'Server error. Please try again later.';
      }
      
      toast({
        title: 'Registration Failed',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = () => {
    toast({
      title: 'Google Sign Up',
      description: 'Google sign up functionality would be implemented here.',
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleGithubSignUp = () => {
    toast({
      title: 'GitHub Sign Up',
      description: 'GitHub sign up functionality would be implemented here.',
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
  };

  return (
    <Box minH="100vh" bg="gray.50" py={8}>
      <Helmet>
        <title>Create Account | SRS</title>
        <meta name="description" content="Create an SRS account and start running GPU-accelerated CFD simulations in the cloud — no hardware required." />
        <link rel="canonical" href="https://srs.numericalai.net/create-user" />
        <meta property="og:title" content="Create Account | SRS" />
        <meta property="og:description" content="Create an SRS account and start running GPU-accelerated CFD simulations in the cloud — no hardware required." />
        <meta property="og:url" content="https://srs.numericalai.net/create-user" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SRS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Create Account | SRS" />
        <meta name="twitter:description" content="Create an SRS account and start running GPU-accelerated CFD simulations in the cloud — no hardware required." />
      </Helmet>
      <Container maxW="lg">
        <VStack spacing={8}>
          {/* Header */}
          <VStack spacing={4} textAlign="center">
            <HStack 
              spacing={1} 
              cursor="pointer" 
              onClick={() => navigate('/')} 
              _hover={{ opacity: 0.8 }} 
              transition="opacity 0.2s"
              borderRadius="md"
              p={2}
              _active={{ transform: "scale(0.98)" }}
            >
              <Image
                src="/simple-logo.png"
                alt="SRS Logo"
                boxSize={14}
                objectFit="contain"
              />
              <Box>
                <Heading size="lg" color="brand.500">SRS</Heading>
                <Text fontSize="sm" color="gray.600">Scale-Resolving Simulation at Scale</Text>
              </Box>
            </HStack>
            <Heading as="h1" size="lg">Create Your Account</Heading>
            <Text color="gray.600">Join thousands of researchers using GPU-powered simulations</Text>
          </VStack>

          {/* Create Account Form */}
          <Card w="full" shadow="lg">
            <CardBody p={8}>
              <form onSubmit={handleSubmit}>
                <VStack spacing={6}>
                  {/* Personal Information */}
                  <Turnstile
                            sitekey={TURNSTILE_SITE_KEY}
                            onVerify={(token) => setTurnstileToken(token)}
                          />
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                    <FormControl isRequired>
                      <FormLabel>First Name</FormLabel>
                      <InputGroup>
                        <Input
                          type="text"
                          placeholder="Enter your first name"
                          value={formData.firstName}
                          onChange={(e) => handleInputChange('firstName', e.target.value)}
                          size="lg"
                        />
                      </InputGroup>
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>Last Name</FormLabel>
                      <InputGroup>
                        <Input
                          type="text"
                          placeholder="Enter your last name"
                          value={formData.lastName}
                          onChange={(e) => handleInputChange('lastName', e.target.value)}
                          size="lg"
                        />
                      </InputGroup>
                    </FormControl>
                  </SimpleGrid>

                  <FormControl isRequired>
                    <FormLabel>Email Address</FormLabel>
                    <InputGroup>
                      <Input
                        type="email"
                        placeholder="Enter your email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        size="lg"
                      />
                    </InputGroup>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Organization</FormLabel>
                    <Input
                      type="text"
                      placeholder="University, Company, or Institution"
                      value={formData.organization}
                      onChange={(e) => handleInputChange('organization', e.target.value)}
                      size="lg"
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Role</FormLabel>
                    <Select
                      placeholder="Select your role"
                      value={formData.role}
                      onChange={(e) => handleInputChange('role', e.target.value)}
                      size="lg"
                    >
                      <option value="researcher">Researcher</option>
                      <option value="student">Student</option>
                      <option value="professor">Professor</option>
                      <option value="engineer">Engineer</option>
                      <option value="scientist">Scientist</option>
                      <option value="other">Other</option>
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Research Area</FormLabel>
                    <Select
                      placeholder="Select your research area"
                      value={formData.researchArea}
                      onChange={(e) => handleInputChange('researchArea', e.target.value)}
                      size="lg"
                    >
                      <option value="fluid-dynamics">Fluid Dynamics</option>
                      <option value="molecular-dynamics">Molecular Dynamics</option>
                      <option value="climate-modeling">Climate Modeling</option>
                      <option value="quantum-computing">Quantum Computing</option>
                      <option value="machine-learning">Machine Learning</option>
                      <option value="other">Other</option>
                    </Select>
                  </FormControl>

                  {/* Password Fields
                  <FormControl isRequired>
                    <FormLabel>Password</FormLabel>
                    <InputGroup size="lg">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Create a password"
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                      />
                      <InputRightElement>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </Button>
                      </InputRightElement>
                    </InputGroup>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Confirm Password</FormLabel>
                    <InputGroup size="lg">
                      <Input
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="Confirm your password"
                        value={formData.confirmPassword}
                        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      />
                      <InputRightElement>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                        </Button>
                      </InputRightElement>
                    </InputGroup>
                  </FormControl>*/}

                  {/* Checkboxes */}
                  <VStack spacing={4} w="full" align="start">

                    {/*
                    <Checkbox
                      isChecked={formData.job_notification}
                      onChange={(e) => handleInputChange('job_notification', e.target.checked)}
                      colorScheme="brand"
                    >
                      Receive notification on job completion
                    </Checkbox>
                    */}

                    <Checkbox
                      isChecked={formData.credit_expiry_notification}
                      onChange={(e) => handleInputChange('credit_expiry_notification', e.target.checked)}
                      colorScheme="brand"
                    >
                      Notify me at 90% credit usage
                    </Checkbox>


                    <Checkbox
                      isChecked={formData.subscribeNewsletter}
                      onChange={(e) => handleInputChange('subscribeNewsletter', e.target.checked)}
                      colorScheme="brand"
                    >
                      Subscribe to newsletters
                    </Checkbox>

                    <Checkbox
                      isChecked={formData.agreeToTerms}
                      onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
                      colorScheme="brand"
                    >
                      I agree to the{' '}
                      <Link color="brand.500" href="#" isExternal>
                        Terms of Service
                      </Link>{' '}
                      and{' '}
                      <Link color="brand.500" href="#" isExternal>
                        Privacy Policy
                      </Link>
                    </Checkbox>

                    <Text fontSize="xs" color="gray.500" fontStyle="italic">
                      A <strong>system-generated password (User Secret)</strong> will be sent to your email from srs.numericalai.net. Please check your spam folder and add this address to your contacts.
                    </Text>
                    
                  </VStack>

                  <Button
                    type="submit"
                    colorScheme="brand"
                    size="lg"
                    w="full"
                    isLoading={isLoading}
                    loadingText="Creating Account..."
                    disabled={!turnstileToken}   // 👈 disable if no token

                  >
                    Create Account
                  </Button>
                </VStack>
              </form>

              <VStack spacing={4} mt={6}>
                <Divider />
                {/*
                <VStack spacing={3} w="full">
                  <Button
                    w="full"
                    variant="outline"
                    leftIcon={<FaGoogle />}
                    onClick={handleGoogleSignUp}
                  >
                    Sign up with Google
                  </Button>
                  <Button
                    w="full"
                    variant="outline"
                    leftIcon={<FaGithub />}
                    onClick={handleGithubSignUp}
                  >
                    Sign up with GitHub
                  </Button>
                </VStack>
                */}
              </VStack>
            </CardBody>
          </Card>

          {/* Sign In Link */}
          <HStack spacing={1}>
            <Text color="gray.600">Already have an account?</Text>
            <Link as={RouterLink} to="/signin" color="brand.500" fontWeight="semibold">
              Sign in
            </Link>
          </HStack>
        </VStack>
      </Container>

      {/* Success Modal */}
      <Modal isOpen={isSuccessOpen} onClose={() => { onSuccessClose(); navigate('/signin'); }} isCentered closeOnOverlayClick={false}>
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent mx={4}>
          <ModalHeader>
            <HStack spacing={2}>
              <Text fontSize="2xl">🎉</Text>
              <Text>Account Created Successfully!</Text>
            </HStack>
          </ModalHeader>

          <ModalBody pb={4}>
            <VStack spacing={4} align="stretch">
              <Text color="gray.700">
                Your account has been created. A <strong>User Secret</strong> (your login password) has been sent to:
              </Text>

              <Box bg="brand.50" border="1px" borderColor="brand.200" borderRadius="md" px={4} py={3}>
                <Text fontWeight="bold" color="brand.700">{registeredEmail}</Text>
              </Box>

              <Alert status="info" borderRadius="md" fontSize="sm">
                <AlertIcon />
                <Box>
                  <Text fontWeight="semibold">How to sign in</Text>
                  <Text mt={1}>
                    Use your email and the <strong>User Secret</strong> from the email as your password on the sign-in page.
                  </Text>
                </Box>
              </Alert>

              <Text fontSize="sm" color="gray.500">
                Didn't receive the email? Check your spam/junk folder and add <strong>srs.numericalai.net</strong> to your contacts.
              </Text>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme="brand"
              w="full"
              onClick={() => { onSuccessClose(); navigate('/signin'); }}
            >
              Go to Sign In
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CreateUser; 